import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Breadcrumbs from '../../components/Breadcrumbs';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { ArrowRight, CheckCircle, Shield, FileCheck } from 'lucide-react';

const AerospaceTrade = () => {
  return (
    <div>
      <SEO 
        title="Aerospace & Defense Trade Compliance Services"
        description="Expert ITAR, EAR, and export control compliance for aerospace and defense manufacturers. Navigate complex regulations for military and commercial aviation products."
        keywords="aerospace trade compliance, ITAR aerospace, defense export controls, aviation regulations, military export licensing"
        path="/industries/aerospace"
      />
      <Header />
      <Breadcrumbs items={[
        { label: 'Services', path: '/services' },
        { label: 'Aerospace & Defense', path: '/industries/aerospace' }
      ]} />
      
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Aerospace & Defense Trade Compliance
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              The aerospace and defense industry faces the most stringent export control requirements. Our specialized compliance services help you navigate ITAR, USML, and sensitive technology transfer regulations.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Aerospace-Specific Challenges</h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-slate-50 rounded-lg p-8">
              <Shield className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">ITAR Compliance</h3>
              <p className="text-gray-700">Defense articles on the USML require strict ITAR compliance including registration, TAAs, and stringent technology transfer controls.</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-8">
              <FileCheck className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Dual-Use Aerospace</h3>
              <p className="text-gray-700">Commercial aerospace items may still be EAR-controlled, requiring careful ECCN classification and compliance with export licensing requirements.</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Technology Transfer</h3>
              <p className="text-gray-700">Sharing technical data with foreign partners, even domestically, triggers deemed export requirements and potential license needs.</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">International Partnerships</h3>
              <p className="text-gray-700">Joint ventures and co-development with foreign entities require Technical Assistance Agreements and careful oversight.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Our Aerospace Compliance Services</h2>
          
          <div className="max-w-4xl mx-auto space-y-6">
            {[
              'ITAR registration and compliance program development',
              'USML vs CCL classification determinations',
              'Technical Assistance Agreement (TAA) preparation',
              'Manufacturing License Agreement (MLA) support',
              'Deemed export assessments for foreign national employees',
              'Export license applications (DSP-5, DSP-73, etc.)',
              'Commodity jurisdiction (CJ) determinations',
              'Technology control plans and procedures'
            ].map((service, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-lg text-gray-800">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-700">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Aerospace Trade Compliance Consultation
          </h2>
          <p className="text-xl text-slate-100 mb-10 max-w-2xl mx-auto">
            Let's discuss your aerospace compliance challenges and develop solutions that protect your operations while enabling international business.
          </p>
          <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Schedule Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AerospaceTrade;